import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatchWithSimpleAlign,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class QrCodePropsBuilder extends PropsBuilder<'qrcode'> {
  override setParentContentProps = (
    elementProps: PartialPropsOf<'qrcode'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'qrcode'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatchWithSimpleAlign());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)
    ) {
      this.patchProps({ content: { title: { align: overrides.label.align } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'qrcode'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'qrcode'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({ style: { code: undefined } });
      return this;
    }

    const { fieldLabel, fieldBox } = overrides;

    if (shouldPatchProp(fieldLabel?.color, 'style.code.foreground', elementProps, initialValues)) {
      this.patchProps({ style: { code: { foreground: fieldLabel.color } } });
    }
    if (shouldPatchProp(fieldBox?.fill, 'style.code.background', elementProps, initialValues)) {
      this.patchProps({ style: { code: { background: fieldBox.fill } } });
    }

    return this;
  };

  constructor(props: PartialPropsOf<'qrcode'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
